import { all, call, put, takeEvery } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { reportError } from 'helpers/error-logging';
import { userSettings, userSmsSubscriptionStatus as userSmsSubscriptionStatusEntity } from 'store/saga/entities/bound';
import { createErrorSnackbar } from 'components/Snackbar';
import { createInfoBanner } from 'components/Banner';
import snakeCase from 'lodash/snakeCase';
import { parseNumber } from 'helpers/core';
import messages from './Notifications.messages';

export const UPDATE_NOTIFICATIONS = 'venuePortal/notifications/UPDATE_NOTIFICATIONS';
export const updateNotifications = (formValues, asyncFns) => ({ type: UPDATE_NOTIFICATIONS, formValues, asyncFns });

export function* doUpdateNotifications(client, action) {
  try {
    const {
      formValues: {
        userId,
        currentReceiveNotifications,
        currentUserSmsSubscriptionStatus,
        currentUserPhoneNumber,
        receiveNotifications,
        userSmsSubscriptionStatus,
        userPhoneNumber,
      },
      asyncFns: { resolveSubmit },
    } = action;
    const settings = Object.entries(currentReceiveNotifications).reduce((updates, [type, currentValues]) => {
      // updates venues that have been added
      currentValues
        .filter(venueId => !receiveNotifications[type].includes(venueId))
        .forEach(venueId => {
          updates.push({
            userId,
            venueId: parseNumber(venueId),
            settingName: snakeCase(type),
            settingValue: 'yes',
          });
        });

      // updates venues that have been removed
      receiveNotifications[type]
        .filter(venueId => !currentValues.includes(venueId))
        .forEach(venueId => {
          updates.push({
            userId,
            venueId: parseNumber(venueId),
            settingName: snakeCase(type),
            settingValue: 'no',
          });
        });

      return updates;
    }, []);

    if (currentUserSmsSubscriptionStatus !== userSmsSubscriptionStatus || currentUserPhoneNumber !== userPhoneNumber) {
      // If user tries to delete phone number from input, disable, and save, we default to the initial phone value.
      // This prevents saving an empty string to the database and allows user to opt back in without re-entering phone number.
      // Check that phone number is not falsy to prevent submitting undefined number.
      if (!!currentUserPhoneNumber || !!userPhoneNumber) {
        const phone = currentUserPhoneNumber || userPhoneNumber;
        const countryCode = phone.substring(0, 2);
        const phoneNumber = phone.substring(2);
        const smsSettings = {
          countryCode,
          phoneNumber,
          optIn: currentUserSmsSubscriptionStatus,
        };
        yield call(fetchEntity, userSmsSubscriptionStatusEntity, [client, { smsSettings, userId }]);
      }
    }

    yield call(fetchEntity, userSettings, [client, { settings, userId }]);
    yield put(createInfoBanner(messages.updateSuccess));
    yield call(resolveSubmit);
  } catch (error) {
    const {
      asyncFns: { rejectSubmit },
    } = action;
    const msg = error.response?.data?.message || messages.updateError;
    yield all([put(createErrorSnackbar(msg, {}, { skipIntl: true })), call(rejectSubmit), call(reportError, error)]);
  }
}

export function* handleRouteEnter(client) {
  try {
    yield all([
      call(fetchEntity, userSettings, [client]),
      call(fetchEntity, userSmsSubscriptionStatusEntity, [client]),
    ]);
  } catch (error) {
    yield all([put(createErrorSnackbar(messages.loadError)), call(reportError, error)]);
  }
}

export function* watchActions(client) {
  yield takeEvery(UPDATE_NOTIFICATIONS, doUpdateNotifications, client);
}
